import { Dayjs } from "dayjs";
import { AffiliateDeposit } from "./Affiliate";
import type { CouponCode } from "./CouponCode";
import { CouponTerm } from "./CouponTerm";
import { EnableablePreference } from "./EnableablePreference";
import { PurchaseRule } from "./PurchaseRule";
import { SelectedExperiences } from "./SelectedExperiences";
import { SelectedMerchandise } from "./SelectedMerchandise";
import { Tag } from "./Tag";
import { DelegateRole, UserRole, UserRolesCopy } from "./User";

// Combined roles array in specific display order: Administrator, Reservationist, Reservationist Lite, Customer
export type CouponRole = DelegateRole.SELLER_ADMIN | DelegateRole.RESERVATION | DelegateRole.RESERVATION_LITE | DelegateRole.AFFILIATE | UserRole.TRAVELER;

export interface Coupon {
    id?: ID;
    name: string;
    excludeTaxFee?: boolean;
    source: CouponSource;
    limit: number | null;
    tags: Tag[];
    seller: { id: ID };
    purchaseRule?: PurchaseRule;
    purchaseRulePermanentId?: ID;
    type: CouponType;
    updatedAt?: Dayjs;
    codes?: CouponCode[];
    triggers?: any;
    rewards?: any;
    uses?: number;
    experiences?: SelectedExperiences;
    merchandise?: SelectedMerchandise;
    algorithm: CouponAlgorithm;
    amount: number;
    terms?: CouponTerm[];
    giftPurchaseItem?: { id: ID };
    paymentMethod?: string;
    permissions: CouponRole[] | null | undefined; // Specifies the roles allowed to use the coupon. `null` or `undefined`: All roles are allowed. Dev should use isNil to check for both null and undefined, when using this field.
}

export enum CouponSource {
    STORE_CREDIT = "store_credit",
    GIFT = "gift",
    XOLA = "xola",
    MEMBERSHIP = "membership",
    GROUPON = "groupon",
}

export const COUPON_ROLES: CouponRole[] = [
    DelegateRole.SELLER_ADMIN,
    DelegateRole.RESERVATION,
    DelegateRole.RESERVATION_LITE,
    DelegateRole.AFFILIATE,
    UserRole.TRAVELER,
];
export const COUPON_ROLES_COPY = {
    ...UserRolesCopy,
    [DelegateRole.AFFILIATE]: "Authenticated Agent",
};

export interface DiscountCoupon extends Coupon {
    amount: number;
}

export interface VoucherCoupon extends DiscountCoupon {
    purchaseAmount: number;
    maxDemographics: number;
    paymentMethod: string;
}

export interface BxgyCoupon extends DiscountCoupon {
    qualifyingQuantity: number;
    eligibleQuantity: number;
}

export enum CouponType {
    DISCOUNT = "discount",
    PERSON = "person",
    UNLOCK = "unlock",
    VOUCHER = "voucher",
    BXGY = "bxgy",
}

export const CouponTypeCopy: { [key in CouponType]: string } = {
    [CouponType.DISCOUNT]: "Discount",
    [CouponType.PERSON]: "Free",
    [CouponType.UNLOCK]: "Unlock",
    [CouponType.VOUCHER]: "Voucher",
    [CouponType.BXGY]: "Buy X Get Y",
};

export enum CouponAlgorithm {
    PERCENT = "percent",
    ABSOLUTE = "absolute",
}

export enum CouponScope {
    PER_PERSON = "per_person",
    PER_EQUIPMENT = "per_equipment",
    PER_BOOKING = "per_booking",
    PER_MERCHANDISE = "per_merchandise",
}

export enum VoucherPaymentMethod {
    GROUPON = "groupon",
    TEG = "TEG",
    LIVING_SOCIAL = "Living social",
    OTHER = "other",
}

export const VoucherPaymentMethodCopy: { [key in VoucherPaymentMethod]: string } = {
    [VoucherPaymentMethod.GROUPON]: "Groupon",
    [VoucherPaymentMethod.TEG]: "TEG",
    [VoucherPaymentMethod.LIVING_SOCIAL]: "Living social",
    [VoucherPaymentMethod.OTHER]: "Other",
};

export interface ApplyCouponCode extends CouponCode {
    amount: string;
    type: CouponType;
    experiences: SelectedExperiences;
    id: ID;
    name: string;
    code: string;
    children?: string[];
    parent?: string;
    delegated: boolean;
    affiliateNotification?: EnableablePreference;
    travelerNotification?: EnableablePreference;
    deposit?: AffiliateDeposit;
    voucher?: boolean;
    purchaseRulesPermanentIds?: string[];
    purchaseRules?: Array<{ id: ID }>;
}
